import { config } from './config';

/**
 * The Room's entire API client.
 *
 * DELIBERATELY NOT `HuacaClient` from @huaca/ui. That client is built around the platform session —
 * refresh cookies, org selection, `/auth/me`, quota headers — none of which exists here, and
 * importing it would drag the whole authentication stack into a page served to anonymous members of
 * the public. This app authenticates one way only: an email OTP, exchanged for a Claro-signed token
 * that grants access to exactly one onboarding.
 *
 * The token lives in `sessionStorage`, not `localStorage`: a Room session should die with the tab.
 * Clients open these links on shared and borrowed devices, and a token that outlives the visit is a
 * credential left behind on someone else's machine.
 */

const TOKEN_KEY = 'claro-room-token';

export function getRoomToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    // Safari in private mode throws on storage access. Losing the session is survivable — the client
    // re-does the OTP; crashing the whole page is not.
    return null;
  }
}

export function setRoomToken(token: string): void {
  try {
    sessionStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* see getRoomToken */
  }
}

export function clearRoomToken(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* see getRoomToken */
  }
}

export class RoomApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'RoomApiError';
    this.status = status;
    this.code = code;
  }
}

interface RoomFetchOptions {
  body?: unknown;
  /** Send the room session token. Omitted for the pre-authentication calls (resolve, otp/*). */
  authenticated?: boolean;
  /**
   * Let the request outlive the page (tab close / navigation). Used by the autosave flush on
   * pagehide: `navigator.sendBeacon` can't carry the `Authorization` header, so a keepalive fetch is
   * the only way to send the Bearer during unload.
   */
  keepalive?: boolean;
}

/**
 * POST to a Room endpoint and return the parsed body.
 *
 * Every Room route is a POST, including the reads — they take an instance id or a bearer token in
 * the body/headers rather than the URL, which keeps ids out of logs (see config.readInstanceIdFromUrl).
 *
 * A 401 clears the stored token before throwing, so a dead session cannot linger and produce a loop
 * of failing requests; the caller re-renders the email gate.
 */
export async function roomFetch<T>(path: string, options: RoomFetchOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (options.authenticated) {
    const token = getRoomToken();
    if (!token) throw new RoomApiError(401, 'UNAUTHORIZED', 'No room session');
    headers['Authorization'] = `Bearer ${token}`;
  }

  let res: Response;
  try {
    res = await fetch(`${config.apiBase}/api/claro/${path}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(options.body ?? {}),
      // No cookies, ever. The Room has no platform session, and sending credentials cross-origin
      // would only widen what a CSRF against this page could reach.
      credentials: 'omit',
      ...(options.keepalive ? { keepalive: true } : {}),
    });
  } catch {
    // Offline, DNS, or a CORS rejection — indistinguishable from JS by design.
    throw new RoomApiError(0, 'NETWORK', 'לא הצלחנו להתחבר. בדקו את החיבור לאינטרנט ונסו שוב.');
  }

  const payload: unknown = await res.json().catch(() => ({}));

  if (!res.ok) {
    if (res.status === 401) clearRoomToken();
    const err = (payload as { error?: { code?: string; message?: string } }).error;
    throw new RoomApiError(res.status, err?.code ?? 'ERROR', err?.message ?? 'משהו השתבש. נסו שוב.');
  }

  return payload as T;
}
